import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Icon, IconStack, LoadingMessage } from 'js/components';
import { faCamera, faSpiderWeb } from '@fortawesome/pro-light-svg-icons';
import SeriesRadarChart, {
  seriesItemsFromLegendMaps,
  ASSESSMENT_SERIES_PALETTE,
  ASSESSMENT_SERIES_FILTER_MAX,
} from 'js/components/Charts/SeriesRadarChart';
import { getCategoryLookups } from 'js/utils/question-set';
import { rubricTeamsToQuestionScores } from 'js/utils/rubric-org-scores';
import { scoreNode } from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgRecommendations/utils';
import { buildOrgHistoryDataMap } from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgAssessmentHistory/buildOrgHistoryDataMap';
import { flattenOrgPickerRows } from 'js/components/OrgNodePicker/flattenOrgPickerRows';
import OrgNodePicker from 'js/components/OrgNodePicker/OrgNodePicker';
import orgPickerStyles from 'js/components/OrgNodePicker/OrgNodePicker.module.scss';
import historyStyles from 'js/routes/Organizations/components/OrgComponent/Visualizations/OrgAssessmentHistory/OrgAssessmentHistory.module.scss';
import { snapshotDisplayName } from './org-snapshot-utils';
import styles from './OrgSnapshots.module.scss';

function NoSnapshotsToCompare({ createPath }) {
  return (
    <div className={styles.noSnapshots}>
      <div className="media loading-message text-muted">
        <IconStack size="4x" fixedWidth className="mr-3">
          <Icon icon={faCamera} />
          <Icon icon={faSpiderWeb} transform="shrink-12 down-1" />
        </IconStack>
        <div className="media-body">
          <p>You don&apos;t have any snapshots yet.</p>
          <p>
            Create a snapshot to get started.
            {createPath ? (
              <>
                {' '}
                <Link to={createPath}>Create a snapshot</Link>
              </>
            ) : null}
          </p>
        </div>
      </div>
    </div>
  );
}

function sortSnapshotsNewestFirst(snapshots = []) {
  return [...snapshots].sort((a, b) => {
    const aTime = new Date(a.snapshot_at || 0).getTime();
    const bTime = new Date(b.snapshot_at || 0).getTime();
    return bTime - aTime;
  });
}

export default function OrgSnapshotCompareRadar({
  organization,
  assessmentTemplate,
  sourceDocument = null,
  snapshots = [],
  isLoading = false,
  error = null,
  createPath = null,
}) {
  const pickerRows = useMemo(
    () => flattenOrgPickerRows(organization),
    [organization]
  );
  const [selectedRow, setSelectedRow] = useState(null);
  const [pinnedKey, setPinnedKey] = useState(null);
  const [peekedKey, setPeekedKey] = useState(null);

  useEffect(() => {
    if (!pickerRows.length) {
      setSelectedRow(null);
      return;
    }
    setSelectedRow((current) => {
      if (current && pickerRows.some((row) => row.rowKey === current.rowKey)) {
        return current;
      }
      return pickerRows[0];
    });
  }, [pickerRows]);

  const orderedSnapshots = useMemo(
    () => sortSnapshotsNewestFirst(snapshots),
    [snapshots]
  );

  const legendFields = useMemo(
    () =>
      orderedSnapshots.map((snapshot, index) => ({
        key: String(snapshot.id),
        name: snapshotDisplayName(snapshot),
        paletteIndex: index + 1,
      })),
    [orderedSnapshots]
  );

  const seriesDataMaps = useMemo(() => {
    if (!selectedRow || !assessmentTemplate || orderedSnapshots.length === 0) {
      return null;
    }
    const maps = {};
    orderedSnapshots.forEach((snapshot) => {
      const asOf = snapshot.snapshot_at
        ? new Date(snapshot.snapshot_at)
        : new Date();
      const questionScores = rubricTeamsToQuestionScores(
        snapshot.teams,
        sourceDocument,
        asOf
      );
      const scored = scoreNode(selectedRow.node, questionScores, organization);
      maps[String(snapshot.id)] = buildOrgHistoryDataMap(
        assessmentTemplate,
        scored
      );
    });
    return maps;
  }, [
    assessmentTemplate,
    sourceDocument,
    orderedSnapshots,
    organization,
    selectedRow,
  ]);

  useEffect(() => {
    const keys = new Set(legendFields.map((field) => field.key));
    if (pinnedKey && !keys.has(pinnedKey)) {
      setPinnedKey(null);
    }
    if (peekedKey && !keys.has(peekedKey)) {
      setPeekedKey(null);
    }
  }, [legendFields, peekedKey, pinnedKey]);

  const skeletonMap = useMemo(() => {
    if (!assessmentTemplate) {
      return null;
    }
    return buildOrgHistoryDataMap(assessmentTemplate, null);
  }, [assessmentTemplate]);

  const scoredMap =
    seriesDataMaps?.[legendFields[0]?.key] ??
    Object.values(seriesDataMaps ?? {})[0] ??
    null;
  const primaryMap = scoredMap ?? skeletonMap;

  const categoryLookups = useMemo(() => {
    if (!primaryMap) {
      return new Map();
    }
    return getCategoryLookups(
      [{ config: assessmentTemplate?.config }],
      primaryMap.categories
    );
  }, [assessmentTemplate?.config, primaryMap]);

  const categoryColorMap = useMemo(() => {
    const map = {};
    categoryLookups.forEach((value, key) => {
      map[key] = value.palette;
    });
    return map;
  }, [categoryLookups]);

  const handlePin = useCallback((key) => setPinnedKey(key), []);
  const handleClearPin = useCallback(() => setPinnedKey(null), []);
  const handlePeek = useCallback((key) => setPeekedKey(key), []);
  const handleClearPeek = useCallback(() => setPeekedKey(null), []);

  if (error) {
    return (
      <div className={historyStyles.root}>
        <div>Sorry, there was a problem loading the details.</div>
      </div>
    );
  }

  if (orderedSnapshots.length > 0 && !selectedRow) {
    return <LoadingMessage iconSize="2x">Loading data&hellip;</LoadingMessage>;
  }

  const questionSets = primaryMap?.questionSets ?? {};

  return (
    <div className={historyStyles.root}>
      <div className={classNames('manage-org', historyStyles['picker-shell'])}>
        <OrgNodePicker
          className={orgPickerStyles.compact}
          rows={pickerRows}
          selectedRow={selectedRow}
          onSelect={setSelectedRow}
          disabled={!organization}
        />
      </div>
      <SeriesRadarChart
        questionSets={questionSets}
        series={seriesItemsFromLegendMaps(legendFields, seriesDataMaps)}
        categoryLookups={categoryLookups}
        categoryColorMap={categoryColorMap}
        legendHeading="Snapshots"
        seriesPalette={ASSESSMENT_SERIES_PALETTE}
        pinnedKey={pinnedKey}
        activeKey={peekedKey ?? pinnedKey}
        onPin={handlePin}
        onClearPin={handleClearPin}
        onPeek={handlePeek}
        onClearPeek={handleClearPeek}
        isLoading={isLoading && !scoredMap}
        emptyComponent={() => <NoSnapshotsToCompare createPath={createPath} />}
        enableSeriesFilter
        maxSeries={ASSESSMENT_SERIES_FILTER_MAX}
      />
    </div>
  );
}
